import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { BACKEND_URL, useApi, errMsg } from "@/lib/api";

const KIND = { tail: "Exact tail", type: "Aircraft type", stock: "Representative", upload: "Operator photo" };

export function useImageSrc(url) {
  const api = useApi();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) { setSrc(null); return undefined; }
    if (!url.startsWith("/api/aircraft-photos")) {
      setSrc(`${BACKEND_URL}${url}`);
      return undefined;
    }
    let blobUrl;
    let cancelled = false;
    api.get(url.replace(/^\/api/, ""), { responseType: "blob" }).then((r) => {
      if (cancelled) return;
      blobUrl = URL.createObjectURL(r.data);
      setSrc(blobUrl);
    }).catch(() => setSrc(null));
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [api, url]);
  return src;
}

function StaffControls({ option, kind, info, onChanged, testId }) {
  const api = useApi();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("apply_to_tail", "true");
    try {
      await api.post(`/options/${option.id}/photos/${kind}`, fd);
      toast.success(option.tail_number ? `Saved to the ${option.tail_number} photo library` : "Photo saved for this option");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/options/${option.id}/photos/${kind}`);
      toast.success("Uploaded photo removed");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="on-navy absolute top-2 right-2 flex gap-1.5 opacity-100 lg:opacity-0 group-hover/photo:opacity-100 transition-opacity">
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid={`${testId}-file-input`} />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testId}-upload-btn`}
        className="flex items-center gap-1.5 bg-black/70 backdrop-blur px-2.5 py-1.5 text-[0.58rem] tracking-[0.18em] uppercase text-white hover:text-[#5B7FD1] border border-white/15">
        <Upload size={11} /> {busy ? "Uploading…" : "Upload"}
      </button>
      {info?.kind === "upload" && (
        <button type="button" onClick={remove} data-testid={`${testId}-remove-btn`} aria-label="Remove uploaded photo"
          className="bg-black/70 backdrop-blur px-2 py-1.5 text-white hover:text-rose-300 border border-white/15">
          <X size={11} />
        </button>
      )}
    </div>
  );
}

export default function OptionPhoto({ option, kind, label, staff, onChanged, testId }) {
  const info = option.photos?.[kind];
  const src = useImageSrc(option.photo_urls?.[kind]);
  return (
    <figure className="relative overflow-hidden bg-black aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[260px] group/photo" data-testid={testId}>
      {src && <img src={src} alt={label} className="w-full h-full object-cover transition-transform duration-700 group-hover/photo:scale-105" loading="lazy" />}
      {staff && <StaffControls option={option} kind={kind} info={info} onChanged={onChanged} testId={testId} />}
      <figcaption className="on-navy absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black/90 to-transparent">
        <span className="text-[0.58rem] tracking-[0.2em] uppercase text-white">{label}</span>
        {info && <span className="ml-2 text-[0.58rem] tracking-[0.16em] uppercase text-[#C6A55C]" data-testid={`${testId}-source`}>{KIND[info.kind]}</span>}
      </figcaption>
    </figure>
  );
}
